import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WS_URL } from '../api/constant';
import { hasModule } from '../utils/capabilitiesHelper';
import {
  setWsConnectionStatus,
  fetchConversationsRequest,
  fetchConversationRequest,
  selectCurrentConversation,
} from '../store/conversationSlice';

import {
  globalSocket,
  setGlobalSocket,
  disconnectTimeoutRef,
  setDisconnectTimeoutRef,
  reconnectTimeoutRef,
  setReconnectTimeoutRef,
  reconnectAttempts,
  setReconnectAttempts,
  incrementReconnectAttempts,
  isIntentionallyDisconnected,
  setIntentionallyDisconnected,
  socketChannel,
  isLeader,
  setIsLeader,
  leaderElectionTimeout,
  setLeaderElectionTimeout,
  pingIntervalRef,
  setPingIntervalRef,
  disconnectWhatsAppSocket
} from './whatsappSocket/socketManager';

import { getPayloadProcessor } from './whatsappSocket/payloadProcessor';

export { disconnectWhatsAppSocket, isWebSocketConnected, sendMessageViaWebSocket } from './whatsappSocket/socketManager';

export const useWhatsAppSocket = () => {
  const dispatch = useDispatch();
  const idToken = useSelector((state) => state.auth?.user?.tokens?.idToken);
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const hasWhatsApp = useSelector((state) =>
    hasModule(state.capabilities?.capabilities, 'WHATSAPP')
  );

  const processWsPayload = useMemo(() => getPayloadProcessor(dispatch), [dispatch]);

  const connect = useCallback(() => {
    if (!WS_URL || !isAuthenticated || !hasWhatsApp) return;
    if (globalSocket?.readyState === WebSocket.OPEN) return;

    dispatch(setWsConnectionStatus('connecting'));

    const wsUrlWithToken = idToken
      ? `${WS_URL}${WS_URL.includes('?') ? '&' : '?'}token=${encodeURIComponent(idToken)}`
      : WS_URL;

    const ws = new WebSocket(wsUrlWithToken);

    ws.onopen = () => {
      dispatch(setWsConnectionStatus('connected'));
      dispatch(fetchConversationsRequest({ status: "OPEN", limit: 10 }));
      
      setReconnectAttempts(0);

      // Heartbeat to keep connection alive (AWS API Gateway idle timeout is 10 mins)
      if (pingIntervalRef) clearInterval(pingIntervalRef);
      const interval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'ping' }));
        }
      }, 4 * 60 * 1000); // 4 minutes
      setPingIntervalRef(interval);
    };

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        processWsPayload(payload);
        socketChannel.postMessage({ type: 'WS_DATA', payload });
      } catch (error) {
      }
    };

    ws.onclose = () => {
      dispatch(setWsConnectionStatus('disconnected'));
      setGlobalSocket(null);
      if (pingIntervalRef) {
        clearInterval(pingIntervalRef);
        setPingIntervalRef(null);
      }
      if (!isIntentionallyDisconnected) {
        attemptReconnect();
      }
    };

    ws.onerror = (error) => {
      ws.close();
    };

    setGlobalSocket(ws);
  }, [dispatch, idToken, isAuthenticated, hasWhatsApp, processWsPayload]);

  const attemptReconnect = useCallback(() => {
    if (isIntentionallyDisconnected || !isLeader) return;
    
    if (reconnectTimeoutRef) clearTimeout(reconnectTimeoutRef);
    const timeout = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
    incrementReconnectAttempts();
    dispatch(setWsConnectionStatus('reconnecting'));
    setReconnectTimeoutRef(setTimeout(connect, timeout));
  }, [connect, dispatch]);

  const promoteToLeader = useCallback(() => {
    if (isLeader) return;
    setIsLeader(true);
    connect();
  }, [connect]);

  // Handle Tab communication and Leader Election
  useEffect(() => {
    const handleChannelMessage = (event) => {
      const { type, payload } = event.data;
      
      switch (type) {
        case 'PING_LEADER':
          if (isLeader && globalSocket?.readyState === WebSocket.OPEN) {
            socketChannel.postMessage({ type: 'PONG_LEADER' });
          }
          break;
        
        case 'PONG_LEADER':
          if (leaderElectionTimeout) {
            clearTimeout(leaderElectionTimeout);
            setLeaderElectionTimeout(null);
          }
          if (isLeader) {
              setIsLeader(false);
              if (globalSocket) {
                  globalSocket.close();
                  setGlobalSocket(null);
              }
          }
          dispatch(setWsConnectionStatus('connected'));
          break;

        case 'WS_DATA':
          if (!isLeader) {
            processWsPayload(payload);
          }
          break;

        case 'LEADER_LEAVING':
          if (!isLeader) {
            const delay = Math.floor(Math.random() * 500);
            setTimeout(() => {
                socketChannel.postMessage({ type: 'PING_LEADER' });
                setLeaderElectionTimeout(setTimeout(promoteToLeader, 200));
            }, delay);
          }
          break;
          
        default:
          break;
      }
    };

    socketChannel.addEventListener('message', handleChannelMessage);
    socketChannel.postMessage({ type: 'PING_LEADER' });
    setLeaderElectionTimeout(setTimeout(promoteToLeader, 200));

    return () => {
      socketChannel.removeEventListener('message', handleChannelMessage);
    };
  }, [dispatch, processWsPayload, promoteToLeader]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isLeader) {
        socketChannel.postMessage({ type: 'LEADER_LEAVING' });
      }
      disconnectWhatsAppSocket();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const currentConversation = useSelector(selectCurrentConversation);
  const wsConnectionStatus = useSelector((state) => state.conversation?.wsConnectionStatus);

  useEffect(() => {
    if (!isAuthenticated || !hasWhatsApp) {
      disconnectWhatsAppSocket();
      return;
    }
    promoteToLeader();
  }, [isAuthenticated, hasWhatsApp, promoteToLeader]);

  useEffect(() => {
    if (wsConnectionStatus === 'connected' && currentConversation?.id) {
      dispatch(
        fetchConversationRequest({
          conversationId: currentConversation.id,
          background: true,
        })
      );
    }
  }, [wsConnectionStatus, currentConversation?.id, dispatch]);

  useEffect(() => {
    if (disconnectTimeoutRef) {
      clearTimeout(disconnectTimeoutRef);
      setDisconnectTimeoutRef(null);
    }
    
    setIntentionallyDisconnected(false);

    return () => {
      if (disconnectTimeoutRef) clearTimeout(disconnectTimeoutRef);
      setDisconnectTimeoutRef(setTimeout(() => {
        disconnectWhatsAppSocket();
      }, 12 * 60 * 60 * 1000));
    };
  }, [isAuthenticated]);
};
